/** The Pools and releases screen's words. Staff read what a thing does; never a model or a vendor. Pure. */
import type { AdminPools, DeployedPart, DeployedState } from './pools-types';

const NAME: Record<string, string> = {
  'page-work': 'Page work',
  'page-prep': 'Page preparation',
  ocr: 'Page reader',
  understanding: 'Understanding',
  reasoner: 'Reasoner',
  web: 'Web app',
  api: 'API',
  orchestrator: 'Orchestrator',
  'engine-host': 'Engine host',
  watch: 'Health monitor',
};

const DOES: Record<string, string> = {
  'page-work': 'opens customers’ files',
  'page-prep': 'finds blank pages',
  ocr: 'reads the words',
  understanding: 'types and fields',
  reasoner: 'AI review and job setup',
  web: 'the site people use',
  api: 'answers every request',
  orchestrator: 'moves work between stages',
  'engine-host': 'runs each job’s engine',
  watch: 'checks and alerts',
};

/** "Page reader" for `ocr`; a part the screen has no name for keeps its own. */
export function partName(id: string): string {
  return NAME[id] ?? id;
}

/** A few words on what a part does, or nothing for one the screen does not know. */
export function partDoes(id: string): string | null {
  return DOES[id] ?? null;
}

export const stateWord: Record<DeployedState, string> = {
  up: 'Up',
  short: 'Short',
  down: 'Down',
  off: 'Off',
};

export const stateTone: Record<DeployedState, 'success' | 'warning' | 'danger' | 'neutral'> = {
  up: 'success',
  short: 'warning',
  down: 'danger',
  off: 'neutral',
};

/** A build as it is shown: a commit's first eight characters, any other tag whole. */
export function buildWord(build: string | null): string {
  if (!build) return 'unknown build';
  return /^[0-9a-f]{9,40}$/.test(build) ? build.slice(0, 8) : build;
}

export interface PoolsSummary {
  tone: 'success' | 'warning' | 'danger' | 'neutral';
  /** "All up", "1 down", "1 down, 2 short", "Not reporting". */
  headline: string;
  /** Parts that are wanted (not switched off). */
  wanted: number;
  up: number;
  off: number;
  /** The names of what is down, then what is short. */
  trouble: string[];
}

/** The answer to "is everything up?" in a word, and what is not. */
export function summaryOf(view: AdminPools): PoolsSummary {
  const parts: DeployedPart[] = [...view.pools, ...view.services];
  const down = parts.filter((p) => p.state === 'down');
  const short = parts.filter((p) => p.state === 'short');
  const off = parts.filter((p) => p.state === 'off').length;
  const base = {
    wanted: parts.length - off,
    up: parts.filter((p) => p.state === 'up').length,
    off,
    trouble: [...down, ...short].map((p) => partName(p.id)),
  };
  if (view.stale) return { ...base, tone: 'neutral', headline: 'Not reporting' };
  const counts = [
    down.length > 0 ? `${down.length} down` : '',
    short.length > 0 ? `${short.length} short` : '',
  ].filter(Boolean);
  if (counts.length > 0) {
    return { ...base, tone: down.length > 0 ? 'danger' : 'warning', headline: counts.join(', ') };
  }
  return { ...base, tone: 'success', headline: 'All up' };
}

/** Memory in gigabytes to one place: "5.2 GB". */
export function gigabytes(bytes: number): number {
  return Math.round((bytes / 1024 ** 3) * 10) / 10;
}
